import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { generatePKCE, generateState } from '@/lib/pkce';

/**
 * Content Authentication Start Page
 *
 * Initiates OAuth flow for direct browser access to private projects.
 * Redirects to OptiPrime SSO with PKCE parameters.
 */
export default function ContentAuthStart(): JSX.Element {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const projectId = searchParams.get('project_id');
    const returnUrl = searchParams.get('return_url');

    if (!projectId || !returnUrl) {
      console.error('Missing required parameters:', { projectId, returnUrl });
      // TODO: Show error UI
      return;
    }

    initiateContentAuth(projectId, returnUrl);
  }, [searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting to authentication...</p>
      </div>
    </div>
  );
}

/**
 * Initiate content authentication flow
 */
async function initiateContentAuth(projectId: string, returnUrl: string): Promise<void> {
  try {
    // 1. Generate PKCE parameters (reusing existing utility)
    const { codeVerifier, codeChallenge } = await generatePKCE();
    const state = generateState();

    // 2. Store in sessionStorage for callback validation
    sessionStorage.setItem('content_code_verifier', codeVerifier);
    sessionStorage.setItem('content_oauth_state', state);
    sessionStorage.setItem('content_project_id', projectId);
    sessionStorage.setItem('content_return_url', returnUrl);

    // 3. Build OptiPrime authorization URL
    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_OAUTH_CLIENT_ID,
      response_type: 'code',
      redirect_uri: `${window.location.origin}/auth/content-pass`,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });

    // 4. Redirect to OptiPrime
    const authUrl = `${import.meta.env.VITE_AUTH_URL}/oauth/authorize`;
    window.location.href = `${authUrl}?${params}`;
  } catch (error) {
    console.error('Failed to initiate content auth:', error);
    // TODO: Show error UI
  }
}
